/**
 * Header decoration/icon for a preference type category.
 *
 * Belongs here: header SVG or fund list UI.
 * Does not belong here: questionnaire flow.
 */
import type { ComponentType } from 'react'
import { PreferenceTypeId } from '../../setup/types'
import { GoldPreferenceHeaderIcon } from './GoldPreferenceHeaderIcon'
import { GlobalPreferenceHeaderIcon } from './GlobalPreferenceHeaderIcon'
import { SatellitePreferenceHeaderIcon } from './SatellitePreferenceHeaderIcon'
import { DebtPreferenceHeaderIcon } from './DebtPreferenceHeaderIcon'
import { GoalBasedPreferenceHeaderIcon } from './GoalBasedPreferenceHeaderIcon'

export type PreferenceTypeHeaderDecorationVariant = 'default' | 'grouped'

enum PreferenceHeaderIconKind {
  Gold = 'gold',
  Global = 'global',
  Satellite = 'satellite',
  Debt = 'debt',
  GoalBased = 'goalBased'
}

const HEADER_ICON_BY_KIND: Record<
  PreferenceHeaderIconKind,
  ComponentType<{ className?: string }>
> = {
  [PreferenceHeaderIconKind.Gold]: GoldPreferenceHeaderIcon,
  [PreferenceHeaderIconKind.Global]: GlobalPreferenceHeaderIcon,
  [PreferenceHeaderIconKind.Satellite]: SatellitePreferenceHeaderIcon,
  [PreferenceHeaderIconKind.Debt]: DebtPreferenceHeaderIcon,
  [PreferenceHeaderIconKind.GoalBased]: GoalBasedPreferenceHeaderIcon
}

function getPreferenceHeaderIconKind(preferenceTypeId: string): PreferenceHeaderIconKind {
  if (preferenceTypeId === PreferenceTypeId.SILVER_AND_GOLD) {
    return PreferenceHeaderIconKind.Gold
  }
  if (
    preferenceTypeId === PreferenceTypeId.GLOBAL_US ||
    preferenceTypeId === PreferenceTypeId.GLOBAL_NON_CORE ||
    preferenceTypeId === PreferenceTypeId.GLOBAL_OTHER
  ) {
    return PreferenceHeaderIconKind.Global
  }
  if (
    preferenceTypeId === PreferenceTypeId.DEBT_AND_ARBITRAGE ||
    preferenceTypeId === PreferenceTypeId.DEBT
  ) {
    return PreferenceHeaderIconKind.Debt
  }
  if (preferenceTypeId === PreferenceTypeId.SATELLITE) {
    return PreferenceHeaderIconKind.Satellite
  }
  if (
    preferenceTypeId === PreferenceTypeId.ELSS_TAX_SAVINGS ||
    preferenceTypeId === PreferenceTypeId.RETIREMENT ||
    preferenceTypeId === PreferenceTypeId.CHILDREN ||
    preferenceTypeId === PreferenceTypeId.GOAL_BASED
  ) {
    return PreferenceHeaderIconKind.GoalBased
  }
  return PreferenceHeaderIconKind.GoalBased
}

function getPreferenceTypeHeaderIcon(
  preferenceTypeId: string
): ComponentType<{ className?: string }> {
  return HEADER_ICON_BY_KIND[getPreferenceHeaderIconKind(preferenceTypeId)]
}

interface PreferenceTypeHeaderDecorationProps {
  preferenceTypeId: string
  variant?: PreferenceTypeHeaderDecorationVariant
}

export function PreferenceTypeHeaderDecoration({
  preferenceTypeId,
  variant = 'default'
}: PreferenceTypeHeaderDecorationProps) {
  const HeaderIcon = getPreferenceTypeHeaderIcon(preferenceTypeId)
  const className =
    variant === 'grouped'
      ? 'pointer-events-none absolute top-20 right-[-10px] z-[-1] h-[160px] w-[160px] opacity-80'
      : 'absolute top-24 right-[-10px] z-[-1] h-[160px] w-[160px] opacity-50'

  return (
    <div className={className} aria-hidden>
      <HeaderIcon className="h-full w-full" />
    </div>
  )
}
